import {
    Basket,
    CustomOptions,
    ShippingTimeslotMap,
    TaxDetails,
} from "@hermes/api-model-basket";
import {
    Product,
    ProductStock,
    ShippingDelay,
} from "@hermes/api-model-product";
import { NO_SIZE } from "@hermes/hermes-web-fragments-analytics";
import { EcomErrorCodes } from "@hermes/utils-generic-constants";

/**
 * Basket return by ecom
 * Used to type basket set in state and sessionStorage
 */
export interface SerializedBasket {
    subtotal: number;
    grand_total: number;
    tax_amount: number;
    shipping_amount: number;
    items: Array<{ [key: string]: unknown }>;
    gift: { [key: string]: unknown };
    has_sample: boolean;
    has_family_code: boolean;
    sample_skus: string[];
    has_product_threshold: boolean;
    shipping_address: { [key: string]: unknown };
    billing_address: { [key: string]: unknown };
    shipping_method: string;
    shipping_method_label: string;
    default_shipping_method?: string;
    shipping_duration: string;
    available_shipping_methods: { [key: string]: unknown };
    estimate_tax_error: boolean;
    show_tax: { [key: string]: number };
    show_tax_message: { [key: string]: string };
    has_casper: boolean;
    has_shared_stock?: boolean;
    has_recurring: boolean;
    show_message: number;
    shipping_appointment_dates: unknown;
    shipping_appointment_timeslots: ShippingTimeslotMap;
    has_complimentary_delivery: boolean;
    adyen_client_key: string;
    checkout_com_key: string;
    is_personalized: boolean;
    total_items: number;
    taxes_covered: number;
    shipping_delay?: ShippingDelay;
    tax_details?: TaxDetails;
}

/**
 * Ecom can respond lot of differents stuff in case of failure
 * But we need at least an internal_code of error
 */
export interface EcomError {
    internal_code: EcomErrorCodes;
    item?: string;
    code?: number;

    [key: string]: unknown;
}

export interface AddToCartPayload {
    sku: string;
    skuSet?: string;
    templateType?: string;
    displayNotifOnSuccess: boolean;
    displayNotifOnFail?: boolean;
    itemPosition: number;
    sendAnalyticsEvent?: boolean;
    dku?: string;
    urlImage?: string;
    customOptions?: CustomOptions;
    isQuickBuy?: boolean;
    context: string;
    product?: LightProduct;
    currencyCode?: string;
    quickBuyRevamp?: boolean;
}

export interface AddManyToCartItem {
    sku: string;
}

export interface AddManyToCartPayload {
    items: AddManyToCartItem[];

    // Position du CTA global "Add look to cart"
    itemPosition: number;

    context: string;
    templateType?: string;

    displayNotifOnSuccess: boolean;
    displayNotifOnFail?: boolean;

    sendAnalyticsEvent?: boolean;
    quickBuyRevamp?: boolean;
}

export interface AddManyToCartSuccessProperties {
    basket: Basket;
    displayNotifOnSuccess: boolean;
    itemPosition: number;
    skus: string[];
    templateType?: string;
    sendAnalyticsEvent?: boolean;

    context: string;
}

export interface AddToCartSuccessProperties {
    basket: Basket;
    displayNotifOnSuccess: boolean;
    itemPosition: number;
    sku: string;
    templateType?: string;
    sendAnalyticsEvent?: boolean;
    isProductEngraving: boolean;
    context: string;
    product?: LightProduct;
    currencyCode?: string;
}

export interface AddToCartFailureProperties {
    error: EcomError;
    itemPosition: number;
    shouldOpenModal?: boolean;
    context?: string;
    quickBuyRevamp?: boolean;
}

export const productLightDefaults = {
    size: NO_SIZE,
    templateType: "default_value",
};

export interface LightProductAsset {
    url: string;
    /** Note: type is optional to be compatible with `Asset` model (from `Product`) */
    type?: string;
    title?: string;
    posterUrl?: string;
}

export type LightProduct = Pick<
    Product,
    | "sku"
    | "url"
    | "title"
    | "price"
    | "departmentCode"
    | "familyCode"
    | "size"
    | "productCode"
    | "divisionCode"
> & {
    stock: ProductStock | boolean;
    assets?: LightProductAsset[];
} & Partial<Pick<Product, "displayNameList" | "templateType">>;
